export function buildPublicAbsoluteUrl(siteUrl, path) {
    return `${siteUrl.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;
}
function resolvePublicImage(siteUrl, image) {
    return /^https?:\/\//i.test(image)
        ? image
        : buildPublicAbsoluteUrl(siteUrl, image);
}
export function createPublicStructuredData(json) {
    return {
        type: "application/ld+json",
        json,
    };
}
export function createPublicIndexablePolicy(options = {}) {
    return {
        kind: "indexable",
        follow: options.follow ?? true,
        maxImagePreview: options.maxImagePreview ?? "large",
    };
}
export function createPublicNoIndexPolicy(reason, follow = false) {
    return {
        kind: "noindex",
        reason,
        follow,
    };
}
export function formatPublicRobots(policy) {
    if (policy.kind === "noindex")
        return `noindex, ${policy.follow === true ? "follow" : "nofollow"}`;
    const directives = [
        "index",
        policy.follow === false ? "nofollow" : "follow",
    ];
    if (policy.maxImagePreview)
        directives.push(`max-image-preview:${policy.maxImagePreview}`);
    return directives.join(", ");
}
export function createPublicHeadDescriptor(options) {
    const siteUrl = options.site.url.replace(/\/$/, "");
    const canonical = buildPublicAbsoluteUrl(siteUrl, options.canonicalPath);
    const image = resolvePublicImage(siteUrl, options.image ?? options.site.defaultImage);
    const alternates = [...(options.alternates ?? [])];
    const structuredData = [...(options.structuredData ?? [])];
    const openGraph = {
        type: options.openGraphType ?? "website",
        locale: options.htmlLang.replace("-", "_"),
    };
    const twitter = {
        card: options.site.twitterCard ?? "summary_large_image",
    };
    return {
        htmlLang: options.htmlLang,
        title: options.title,
        description: options.description,
        path: options.path,
        canonical,
        indexing: options.indexing ?? createPublicIndexablePolicy(),
        alternates,
        image,
        identity: {
            siteName: options.site.name,
            themeColor: options.site.themeColor,
            author: options.site.author ?? options.site.name,
            applicationName: options.site.applicationName ?? options.site.name,
        },
        structuredData,
        openGraph,
        twitter,
    };
}
function resolveSeoCopy(seo, locale, translate) {
    return {
        title: [
            translate(locale, seo.titleKey),
            ...(seo.titleSuffix ?? []).map((suffix) => suffix.kind === "literal"
                ? suffix.value
                : translate(locale, suffix.key)),
        ].join(" — "),
        description: translate(locale, seo.descriptionKey),
    };
}
export function createPublicSeoResolver(options) {
    const getLocale = (path) => options.site.getLocaleFromPath(path)
        ?? options.site.defaultLocale;
    const createStatusHead = (path, seo, reason) => {
        const normalizedPath = options.site.normalizePath(path);
        const locale = getLocale(normalizedPath);
        const copy = resolveSeoCopy(seo, locale, options.translate);
        return createPublicHeadDescriptor({
            site: options.identity,
            path: normalizedPath,
            htmlLang: locale,
            title: copy.title,
            description: copy.description,
            canonicalPath: normalizedPath,
            indexing: createPublicNoIndexPolicy(reason),
        });
    };
    const resolveNotFoundHead = (path) => createStatusHead(path, options.notFoundSeo, "not-found");
    const resolveUnavailableHead = (path) => createStatusHead(path, options.unavailableSeo, "unavailable");
    const resolveHead = (path) => {
        const normalizedPath = options.site.normalizePath(path);
        const resolution = options.site.resolvePath(normalizedPath);
        if (resolution.type === "not-found")
            return resolveNotFoundHead(normalizedPath);
        if (resolution.type !== "render")
            return createStatusHead(normalizedPath, resolution.type === "app-shell"
                ? options.privateSeo
                : options.redirectSeo ?? options.unavailableSeo, resolution.type === "app-shell" ? "private" : "redirect");
        const copy = resolveSeoCopy(resolution.route.seo, resolution.locale, options.translate);
        return createPublicHeadDescriptor({
            site: options.identity,
            path: resolution.routePath,
            htmlLang: resolution.locale,
            title: copy.title,
            description: copy.description,
            canonicalPath: resolution.canonicalPath,
            indexing: resolution.indexable
                ? resolution.route.seo.indexing ?? createPublicIndexablePolicy()
                : createPublicNoIndexPolicy("private"),
            image: resolution.route.seo.image,
            structuredData: options.structuredData?.(resolution, copy),
            alternates: options.site.shouldApplyHreflang(resolution.routePath)
                ? options.site.buildAlternateHrefs(resolution.routePath, options.identity.url)
                : [],
        });
    };
    return {
        resolveHead,
        resolveNotFoundHead,
        resolveUnavailableHead,
    };
}
export function extendPublicHeadDescriptor(head, extension) {
    return {
        ...head,
        title: extension.title ?? head.title,
        description: extension.description ?? head.description,
        image: extension.image ?? head.image,
        structuredData: extension.structuredData ?? head.structuredData,
        openGraph: head.openGraph
            ? {
                ...head.openGraph,
                type: extension.openGraphType ?? head.openGraph.type,
            }
            : undefined,
    };
}
export function compilePublicHead(head) {
    const nodes = [
        {
            key: "title",
            kind: "title",
            content: head.title,
        },
        {
            key: "meta:name:description",
            kind: "meta",
            name: "description",
            content: head.description,
        },
        {
            key: "meta:name:robots",
            kind: "meta",
            name: "robots",
            content: formatPublicRobots(head.indexing),
        },
        {
            key: "link:canonical",
            kind: "link",
            rel: "canonical",
            href: head.canonical,
        },
        ...head.alternates.map((alternate) => ({
            key: `link:alternate:${alternate.hreflang}`,
            kind: "link",
            rel: "alternate",
            hreflang: alternate.hreflang,
            href: alternate.href,
        })),
        {
            key: "meta:name:theme-color",
            kind: "meta",
            name: "theme-color",
            content: head.identity.themeColor,
        },
        {
            key: "meta:name:author",
            kind: "meta",
            name: "author",
            content: head.identity.author,
        },
        {
            key: "meta:name:application-name",
            kind: "meta",
            name: "application-name",
            content: head.identity.applicationName,
        },
    ];
    if (head.openGraph) {
        nodes.push({
            key: "meta:property:og:type",
            kind: "meta",
            property: "og:type",
            content: head.openGraph.type,
        }, {
            key: "meta:property:og:site_name",
            kind: "meta",
            property: "og:site_name",
            content: head.identity.siteName,
        }, {
            key: "meta:property:og:locale",
            kind: "meta",
            property: "og:locale",
            content: head.openGraph.locale,
        }, {
            key: "meta:property:og:title",
            kind: "meta",
            property: "og:title",
            content: head.title,
        }, {
            key: "meta:property:og:description",
            kind: "meta",
            property: "og:description",
            content: head.description,
        }, {
            key: "meta:property:og:url",
            kind: "meta",
            property: "og:url",
            content: head.canonical,
        });
        if (head.image)
            nodes.push({
                key: "meta:property:og:image",
                kind: "meta",
                property: "og:image",
                content: head.image,
            });
    }
    if (head.twitter) {
        nodes.push({
            key: "meta:name:twitter:card",
            kind: "meta",
            name: "twitter:card",
            content: head.twitter.card,
        }, {
            key: "meta:name:twitter:title",
            kind: "meta",
            name: "twitter:title",
            content: head.title,
        }, {
            key: "meta:name:twitter:description",
            kind: "meta",
            name: "twitter:description",
            content: head.description,
        });
        if (head.image)
            nodes.push({
                key: "meta:name:twitter:image",
                kind: "meta",
                name: "twitter:image",
                content: head.image,
            });
    }
    nodes.push(...head.structuredData.map((structuredData, index) => ({
        key: `structured-data:${index}`,
        kind: "structured-data",
        type: structuredData.type,
        json: structuredData.json,
    })));
    const keys = new Set();
    for (const node of nodes) {
        if (keys.has(node.key))
            throw new Error(`Duplicate public head node '${node.key}'.`);
        keys.add(node.key);
    }
    return nodes;
}
//# sourceMappingURL=index.js.map